import { FC, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { addNewStationAction } from "../../../redux/actions/authAction";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

const schema = Yup.object().shape({
  deviceName: Yup.string().required("Station name is required"),
});

type FormData = Yup.InferType<typeof schema>;

export interface IAddStationDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  populateFor?: {
    id?: number;
    deviceName: string;
  };
}

export const AddStationDrawer: FC<IAddStationDrawerProps> = ({
  open,
  toggleDrawer,
  populateFor,
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const { addStationLoading } = useAppSelector((state) => state.auth);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      deviceName: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        deviceName: populateFor?.deviceName ?? "",
      });
    }
  }, [open, populateFor, reset]);

  const handleSaveStation = async (data: FormData) => {
    try {
      await dispatch(
        addNewStationAction({
          storeId: 1,
          merchantId: 1,
          deviceName: data.deviceName,
        })
      ).unwrap();
      snackbar.showSnackbar("Station added successfully!", "success");
      toggleDrawer();
    } catch (err) {
      snackbar.showSnackbar(
        "Failed to add station. Please try again.",
        "error"
      );
    }
  };

  return (
    <CustomDrawer
      isOpen={open}
      toggleDrawer={toggleDrawer}
      sx={{ width: "28.65rem" }}
    >
      <form onSubmit={handleSubmit(handleSaveStation)}>
        <Box
          sx={{
            paddingInline: "1.857rem",
            paddingTop: "2rem",
            display: "flex",
            flexDirection: "column",
            gap: "2.5rem",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "1.4rem",
              lineHeight: "1.7rem",
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            {populateFor ? "Edit Station" : "Add a New Station"}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={{ mb: 0.5 }}>Station Name</Typography>
            <Controller
              name="deviceName"
              control={control}
              render={({ field, fieldState }) => (
                <CustomTextBox
                  {...field}
                  type="text"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                  placeholder="Enter Station Name"
                />
              )}
            />
          </Box>

          <CustomButton
            sx={{ width: "9.2rem", height: "2.6rem", marginTop: "1rem" }}
            type="submit"
            disabled={addStationLoading}
          >
            {addStationLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Add Station"
            )}
          </CustomButton>
        </Box>
      </form>
    </CustomDrawer>
  );
};
